// Formulaires de la fiche d'une demande sociale (lot L7, M20) : évaluation des critères de la version du dépôt,
// note interne, examen d'un doublon, classement, décision motivée, engagement existant, suivi, pièce, message
// destiné à l'usager. Chaque acte est refait et contrôlé par le serveur (rôle, habilitation, état, auteur).
import type { ConstatCritere, DemandeSociale, NatureSuiviSocial } from '@cc/contrats';
import { CONSTATS_CRITERE, NATURES_SUIVI_SOCIAL } from '@cc/contrats';
import { Champ, FichierChamp, Selection, ZoneTexte } from '@cc/ui';
import { useState } from 'react';
import { useAction } from '../../commun/useAction';
import { Fenetre } from '../achats/Fenetre';
import { useEngagements } from '../budget/api';
import { aujourdhuiIso } from '../proprete/logique';
import {
  classer,
  decider,
  deposerPiece,
  evaluer,
  examinerDoublon,
  INVALIDER_SOCIAL,
  lierEngagement,
  messageUsager,
  noter,
  suivre,
} from './api';

type Props = { demande: DemandeSociale; onFermer: () => void };
const options = (o: Record<string, string>) =>
  Object.entries(o).map(([valeur, libelle]) => ({ valeur, libelle }));

export function FormulaireEvaluation({ demande, onFermer }: Props) {
  const initial = Object.fromEntries(
    demande.criteres.criteres.map((c) => [
      c.code,
      demande.evaluations.find((e) => e.code === c.code)?.constat ?? 'a_verifier',
    ]),
  ) as Record<string, ConstatCritere>;
  const [v, setV] = useState(initial);
  const action = useAction({
    executer: () =>
      evaluer(demande.id, {
        evaluations: Object.entries(v).map(([code, constat]) => ({ code, constat })),
      }),
    succes: 'Évaluation enregistrée ; la proposition est recalculée, la décision reste humaine.',
    invalider: INVALIDER_SOCIAL,
    apres: onFermer,
  });
  return (
    <Fenetre
      titre="Évaluer les critères"
      description={`Critères de la version ${demande.criteres.version}, en vigueur au dépôt de la demande.`}
      libelle="Enregistrer l'évaluation"
      enCours={action.enCours}
      onFermer={onFermer}
      onValider={() => action.lancer(undefined)}
      erreur={action.messageErreur}
    >
      {demande.criteres.criteres.map((c) => (
        <Selection
          key={c.code}
          libelle={`${c.libelle}${c.obligatoire ? '' : ' (facultatif)'}`}
          options={options(CONSTATS_CRITERE)}
          value={v[c.code]}
          onChange={(e) => setV((x) => ({ ...x, [c.code]: e.target.value as ConstatCritere }))}
        />
      ))}
    </Fenetre>
  );
}

export function FormulaireTexte({
  demande,
  onFermer,
  mode,
}: Props & { mode: 'note' | 'classement' | 'message' }) {
  const [texte, setTexte] = useState(mode === 'message' ? (demande.message_usager ?? '') : '');
  const conf = {
    note: {
      titre: 'Note interne',
      description:
        "Note chiffrée, visible des seuls comptes sociaux habilités ; jamais montrée à l'usager ni dans les notifications.",
      libelle: 'Ajouter la note',
      champ: 'Note',
      executer: () => noter(demande.id, texte),
      succes: 'Note interne ajoutée.',
    },
    classement: {
      titre: 'Classer sans suite',
      description: "La demande est classée avec un motif interne ; rien n'est supprimé.",
      libelle: 'Classer',
      champ: 'Motif',
      executer: () => classer(demande.id, texte),
      succes: 'Demande classée sans suite.',
    },
    message: {
      titre: "Message destiné à l'usager",
      description:
        "Seul ce texte, avec le numéro et l'état, est visible du demandeur sur le portail.",
      libelle: 'Enregistrer le message',
      champ: 'Message',
      executer: () => messageUsager(demande.id, texte),
      succes: "Message destiné à l'usager enregistré.",
    },
  }[mode];
  const action = useAction({
    executer: conf.executer,
    succes: conf.succes,
    invalider: INVALIDER_SOCIAL,
    apres: onFermer,
  });
  return (
    <Fenetre
      titre={conf.titre}
      description={conf.description}
      libelle={conf.libelle}
      enCours={action.enCours}
      onFermer={onFermer}
      onValider={() => action.lancer(undefined)}
      erreur={action.messageErreur}
    >
      <ZoneTexte
        libelle={conf.champ}
        value={texte}
        onChange={(e) => setTexte(e.target.value)}
        required
      />
    </Fenetre>
  );
}

export function FormulaireDoublon({ demande, doublonId, onFermer }: Props & { doublonId: string }) {
  const x = demande.doublons.find((d) => d.id === doublonId);
  const [v, setV] = useState({
    conclusion: 'non_doublon' as 'non_doublon' | 'doublon_confirme',
    motif: '',
  });
  const action = useAction({
    executer: () => examinerDoublon(demande.id, doublonId, v.conclusion, v.motif),
    succes: 'Examen du doublon enregistré.',
    invalider: INVALIDER_SOCIAL,
    apres: onFermer,
  });
  return (
    <Fenetre
      titre={`Examiner le rapprochement avec ${x?.demande.numero ?? ''}`}
      description={`Indices : ${(x?.indices ?? []).map((i) => i.libelle).join(' ; ')}. Une homonymie n'est jamais un motif de refus automatique.`}
      libelle="Enregistrer l'examen"
      enCours={action.enCours}
      onFermer={onFermer}
      onValider={() => action.lancer(undefined)}
      erreur={action.messageErreur}
    >
      <Selection
        libelle="Conclusion"
        options={[
          { valeur: 'non_doublon', libelle: 'Pas un doublon' },
          { valeur: 'doublon_confirme', libelle: 'Doublon confirmé' },
        ]}
        value={v.conclusion}
        onChange={(e) => setV((y) => ({ ...y, conclusion: e.target.value as 'non_doublon' }))}
      />
      <ZoneTexte
        libelle="Motif"
        value={v.motif}
        onChange={(e) => setV((y) => ({ ...y, motif: e.target.value }))}
        required
      />
    </Fenetre>
  );
}

export function FormulaireDecision({ demande, onFermer }: Props) {
  const [v, setV] = useState({
    sens: 'accord' as 'accord' | 'refus',
    motif: '',
    nature: '',
    montant: '',
    message: '',
  });
  const action = useAction({
    executer: () =>
      decider(demande.id, {
        sens: v.sens,
        motif: v.motif,
        nature_aide: v.nature || undefined,
        montant_accorde: v.montant ? Number(v.montant) : undefined,
        message_usager: v.message || undefined,
      }),
    succes:
      'Décision enregistrée ; elle conserve la version des critères et la proposition du moment.',
    invalider: INVALIDER_SOCIAL,
    apres: onFermer,
  });
  return (
    <Fenetre
      titre="Décider"
      description={`Proposition : ${demande.proposition.resultat === 'eligible_propose' ? 'critères obligatoires satisfaits' : demande.proposition.resultat === 'non_eligible_propose' ? 'un critère obligatoire non satisfait' : 'évaluation incomplète'}. La décision est la vôtre et exige un motif ; aucun paiement n'est effectué ici.`}
      libelle="Enregistrer la décision"
      enCours={action.enCours}
      onFermer={onFermer}
      onValider={() => action.lancer(undefined)}
      erreur={action.messageErreur}
    >
      <Selection
        libelle="Sens"
        options={[
          { valeur: 'accord', libelle: 'Accorder' },
          { valeur: 'refus', libelle: 'Refuser' },
        ]}
        value={v.sens}
        onChange={(e) => setV((x) => ({ ...x, sens: e.target.value as 'accord' }))}
      />
      <ZoneTexte
        libelle="Motif (interne)"
        value={v.motif}
        onChange={(e) => setV((x) => ({ ...x, motif: e.target.value }))}
        required
      />
      {v.sens === 'accord' ? (
        <Champ
          libelle="Nature de l'aide"
          value={v.nature}
          onChange={(e) => setV((x) => ({ ...x, nature: e.target.value }))}
          required
        />
      ) : null}
      {v.sens === 'accord' ? (
        <Champ
          libelle="Montant accordé (FCFA)"
          inputMode="numeric"
          value={v.montant}
          onChange={(e) => setV((x) => ({ ...x, montant: e.target.value }))}
          facultatif
          aide="Pour une aide financière ; l'engagement de dépense existant se référence ensuite."
        />
      ) : null}
      <ZoneTexte
        libelle="Message destiné à l'usager"
        value={v.message}
        onChange={(e) => setV((x) => ({ ...x, message: e.target.value }))}
        facultatif
      />
    </Fenetre>
  );
}

export function FormulaireEngagement({ demande, onFermer }: Props) {
  const engagements = useEngagements(null);
  const [id, setId] = useState('');
  const action = useAction({
    executer: () => lierEngagement(demande.id, id),
    succes: 'Engagement de dépense référencé ; le paiement suit la chaîne de la dépense.',
    invalider: INVALIDER_SOCIAL,
    apres: onFermer,
  });
  const liste = (engagements.data?.elements ?? []).filter((e) => e.etat === 'engage');
  return (
    <Fenetre
      titre="Référencer l'engagement de dépense"
      description="L'aide financière est portée par un engagement existant de la chaîne de la dépense. Ce module ne verse aucun argent."
      libelle="Référencer"
      enCours={action.enCours}
      onFermer={onFermer}
      onValider={() => action.lancer(undefined)}
      erreur={
        action.messageErreur ??
        (engagements.error ? 'Liste des engagements indisponible avec vos droits.' : null)
      }
    >
      <Selection
        libelle="Engagement"
        options={liste.map((e) => ({
          valeur: e.id,
          libelle: `${e.numero ?? 'sans numéro'} · ${e.montant.toLocaleString('fr-FR')} FCFA`,
        }))}
        optionVide="Choisir un engagement"
        value={id}
        onChange={(e) => setId(e.target.value)}
        required
      />
    </Fenetre>
  );
}

export function FormulaireSuivi({ demande, onFermer }: Props) {
  const [v, setV] = useState({
    nature: 'entretien' as NatureSuiviSocial,
    date: aujourdhuiIso(),
    observation: '',
    cloturer: 'non',
  });
  const action = useAction({
    executer: () =>
      suivre(demande.id, {
        nature: v.nature,
        date_suivi: v.date,
        observation: v.observation,
        cloturer: v.cloturer === 'oui',
      }),
    succes: 'Suivi enregistré.',
    invalider: INVALIDER_SOCIAL,
    apres: onFermer,
  });
  return (
    <Fenetre
      titre="Ajouter un suivi"
      description="Un versement est constaté dans la chaîne de la dépense, jamais effectué ici."
      libelle="Enregistrer le suivi"
      enCours={action.enCours}
      onFermer={onFermer}
      onValider={() => action.lancer(undefined)}
      erreur={action.messageErreur}
    >
      <Selection
        libelle="Nature"
        options={options(NATURES_SUIVI_SOCIAL)}
        value={v.nature}
        onChange={(e) => setV((x) => ({ ...x, nature: e.target.value as NatureSuiviSocial }))}
      />
      <Champ
        libelle="Date"
        type="date"
        value={v.date}
        onChange={(e) => setV((x) => ({ ...x, date: e.target.value }))}
        required
      />
      <ZoneTexte
        libelle="Observation (confidentielle)"
        value={v.observation}
        onChange={(e) => setV((x) => ({ ...x, observation: e.target.value }))}
        required
      />
      <Selection
        libelle="Clôturer le suivi"
        options={[
          { valeur: 'non', libelle: 'Non' },
          { valeur: 'oui', libelle: 'Oui, le suivi est terminé' },
        ]}
        value={v.cloturer}
        onChange={(e) => setV((x) => ({ ...x, cloturer: e.target.value }))}
      />
    </Fenetre>
  );
}

export function FormulairePiece({ demande, onFermer }: Props) {
  const [fichiers, setFichiers] = useState<File[]>([]);
  const [type, setType] = useState('justificatif');
  const action = useAction({
    executer: () => deposerPiece(demande.id, fichiers[0] as File, type),
    succes: 'Pièce déposée (confidentielle).',
    invalider: INVALIDER_SOCIAL,
    apres: onFermer,
  });
  return (
    <Fenetre
      titre="Déposer une pièce"
      description="Pièce confidentielle : invisible des écrans de documents généraux, servie seulement sous habilitation sociale."
      libelle="Déposer"
      enCours={action.enCours}
      onFermer={onFermer}
      onValider={() => action.lancer(undefined)}
      erreur={action.messageErreur}
    >
      <Selection
        libelle="Type de pièce"
        options={[
          { valeur: 'justificatif', libelle: 'Justificatif' },
          { valeur: 'piece_identite', libelle: "Pièce d'identité" },
          { valeur: 'decision_signee', libelle: 'Décision signée' },
          { valeur: 'autre', libelle: 'Autre' },
        ]}
        value={type}
        onChange={(e) => setType(e.target.value)}
      />
      <FichierChamp
        libelle="Fichier (PDF, JPEG ou PNG)"
        fichiers={fichiers}
        onFichiers={setFichiers}
        multiple={false}
        accept=".pdf,.jpg,.jpeg,.png"
        tailleMax={20 * 1024 * 1024}
        required
      />
    </Fenetre>
  );
}
